import { useRef, useState } from 'react';
import { PenLine, Trash2, Upload, UserRound } from 'lucide-react';
import {
  useUploadStaffImageMutation,
  useRemoveStaffImageMutation,
  getApiError,
  type StaffMember,
} from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  A STAFF MEMBER'S PHOTO AND SIGNATURE

  Why the signature is here and not drawn on the report card
  ──────────────────────────────────────────────────────────
  It is uploaded once per person and reused on every card they sign, because
  signing forty report cards by hand is the job this is meant to remove. The
  card then carries the signature of whoever WROTE the remark — not of whoever
  holds the role today, so a card reprinted in two years still shows the
  teacher who actually taught the child.

  Why a signature wants a transparent PNG, and what happens if it is not
  ─────────────────────────────────────────────────────────────────────
  It is printed over a ruled line. A photograph of a signature on white paper
  prints as a white rectangle sitting on the rule, which looks like a mistake.
  The screen says so, and shows the upload against the same rule it will print
  against, so a bad scan is obvious here rather than on forty sheets.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function StaffImagesDialog({
  open,
  onClose,
  institutionId,
  member,
}: {
  open: boolean;
  onClose: () => void;
  institutionId: string;
  member: StaffMember | null;
}) {
  const [upload, uploadState] = useUploadStaffImageMutation();
  const [remove, removeState] = useRemoveStaffImageMutation();
  const [busyKind, setBusyKind] = useState<'photo' | 'signature' | null>(null);

  const send = async (kind: 'photo' | 'signature', file: File) => {
    if (!member) return;
    setBusyKind(kind);
    await upload({ institutionId, userId: member.id, kind, file });
    setBusyKind(null);
  };

  const error = uploadState.error ?? removeState.error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={member ? `${member.fullName}'s photo and signature` : 'Photo and signature'}
      description="The signature is printed on every report card this person signs."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Slot
          label="Passport photo"
          hint="For the staff record. A head-and-shoulders photo."
          icon={<UserRound size={18} />}
          url={member?.photoUrl ?? null}
          busy={busyKind === 'photo' || (removeState.isLoading && busyKind === null)}
          onPick={(file) => void send('photo', file)}
          onRemove={member ? () => void remove({ institutionId, userId: member.id, kind: 'photo' }) : undefined}
        />

        <Slot
          label="Signature"
          hint="A PNG with a transparent background prints best — it sits on the ruled line rather than covering it."
          icon={<PenLine size={18} />}
          url={member?.signatureUrl ?? null}
          onRule
          busy={busyKind === 'signature'}
          onPick={(file) => void send('signature', file)}
          onRemove={member ? () => void remove({ institutionId, userId: member.id, kind: 'signature' }) : undefined}
        />

        {error && (
          <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
            {getApiError(error)}
          </p>
        )}
      </div>
    </Modal>
  );
}

function Slot({
  label,
  hint,
  icon,
  url,
  onRule,
  busy,
  onPick,
  onRemove,
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  url: string | null;
  /** Preview against a rule, the way it will print. */
  onRule?: boolean;
  busy?: boolean;
  onPick: (file: File) => void;
  onRemove?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-bold text-foreground">{label}</p>
          <p className={cn(styles.meta, 'mt-0.5 leading-relaxed')}>{hint}</p>
        </div>
      </div>

      {/*
        Shown on white regardless of theme, and over a rule for a signature —
        because that is what it will be printed on, and a signature that reads
        beautifully on a dark panel can be invisible on paper.
      */}
      <div
        className={cn(
          'mt-2.5 flex h-[76px] items-end justify-center overflow-clip rounded-md border border-border bg-white px-3',
          onRule ? 'pb-0' : 'items-center py-2',
        )}
      >
        {url ? (
          <img
            src={url}
            alt=""
            className={cn('object-contain', onRule ? 'max-h-[58px] max-w-full' : 'max-h-full max-w-full')}
          />
        ) : (
          <span className="self-center text-[11px] text-neutral-400">Nothing uploaded yet</span>
        )}
      </div>
      {onRule && <div className="mx-3 border-b border-black" />}

      <div className="mt-2.5 flex items-center gap-2">
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            /* Cleared, or picking the SAME file twice after a bad crop fires
               no change event and looks like the upload was ignored. */
            e.target.value = '';
          }}
        />
        <Button variant="secondary" size="sm" loading={busy} onClick={() => input.current?.click()}>
          <Upload size={13} className="mr-1" />
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url && onRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 size={13} className="mr-1" />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
